/**
 * <custom-dialog> — the shared popup/dialog custom element.
 *
 * Extracted from 0-product-content.js so it can load on its own (a few KB) on
 * non-PDP pages that need the popup (Complete the Look + Mix & Match on product
 * cards) WITHOUT the whole product-page bundle. On the product page this file
 * loads BEFORE 0-product-content.js, whose ProductInfoModal subclasses
 * window.CustomDialog. Every DOM/registry touch is guarded so a missing element
 * or a double-load can never throw and break the rest of the page scripts.
 */
(function () {
  "use strict";
  if (window.CustomDialog) return;

  try {
class CustomDialog extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.dialog = this.querySelector('.popup__content');
    this.opener = this.querySelector('.popup__opener');
    this.closeButton = this.querySelector('.popup__close');
    this.defaultStyle = this.dataset.defaultStyle || this.dataset.style || 'drawer';
    this._boundHandleDocumentClick = this.handleDocumentClick.bind(this);

    if (this.dialog) {
      this.dialog.addEventListener('click', (e) => {
        if (e.target === e.currentTarget) {
          this.closeModal();
        }
      });
      
      // Add click event for the close button if it exists
      if (this.closeButton) {
        this.closeButton.addEventListener('click', () => this.closeModal());
      }
    }

    this.opener && this.opener.addEventListener('click', () => this.openModal({ mode: 'default' }));
  }

  connectedCallback() {
    document.addEventListener('click', this._boundHandleDocumentClick);
    this._sortProducts();

    // Defer-load the M&M overflow once the browser is idle (after critical
    // content) so the opener preview + popup are complete before the user opens,
    // without competing with page-load. openModal also calls it as a guard for a
    // very fast open; _loadMmOverflow is idempotent.
    const idle = window.requestIdleCallback
      ? (fn) => requestIdleCallback(fn, { timeout: 3000 })
      : (fn) => setTimeout(fn, 500);
    if (document.readyState === 'complete') {
      idle(() => this._loadMmOverflow());
    } else {
      window.addEventListener('load', () => idle(() => this._loadMmOverflow()), { once: true });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._boundHandleDocumentClick);
  }

  handleDocumentClick(event) {
    const target = event.target;

    if (target.closest('.popup__close')) {
      this.closeModal();
      return;
    }

    if (target.closest('.ctl-atc-view-cart')) {
      this.closeModal();
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:open'));
      return;
    }
  }

  setPresentationMode(mode = 'default') {
    const isAtcMode = mode === 'atc';
    const style = isAtcMode ? 'centered' : this.defaultStyle;

    this.dataset.style = style;
    this.classList.toggle('popup--atc-mode', isAtcMode);
    this.classList.toggle('popup--default-mode', !isAtcMode);

    // Horizontal cards use a single native <select>. In ATC mode, OOS variants
    // should not be selectable (so the user can't submit a sold-out variant).
    // We toggle hidden+disabled on each <option data-available="false">; if the
    // current value just became hidden, we move the selection to the first
    // available option and dispatch change so product-form updates the price /
    // notify-me link accordingly.
    this.querySelectorAll('.product-card--mm select.product-variant-id').forEach(select => {
      const options = [...select.options];
      options.forEach(opt => {
        const isOos = opt.dataset.available === 'false';
        if (isAtcMode && isOos) {
          opt.hidden = true;
          opt.disabled = true;
        } else {
          opt.hidden = false;
          opt.disabled = false;
        }
      });

      if (isAtcMode) {
        const current = select.options[select.selectedIndex];
        if (!current || current.hidden) {
          const firstVisible = options.find(o => !o.hidden);
          if (firstVisible) {
            select.value = firstVisible.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    });
  }

  openModal({ mode = 'default' } = {}) {
    if (this.isOpen) return;
    this._loadMmOverflow();   // lazy-fetch any M&M cards held back by the 20-handle budget
    this.setPresentationMode(mode);
    this._sortProducts();
    if (mode === 'atc') {
      this._applyAtcProductLimit();
    } else {
      this._resetProductLimit();
    }
    document.querySelector('body').classList.add('modal-open');
    this.dialog.showModal();
    this.isOpen = true;

    if (this.classList.contains('ctl-new__dialog')) {
      const visibleCards = this.querySelectorAll(
        '.product-card--mm:not(.product-card--mm-atc-hidden):not(.product-card--mm-atc-price-excluded):not(.product-card--mm-hidden)'
      );
      const shownHandles = [...visibleCards].map((c) => c.dataset.handle).filter(Boolean);
      const productHandle =
        window.location.pathname.split('/products/')[1]?.split('/')[0]?.split('?')[0] || null;
      window.mmTrack?.('popup_opened', {
        trigger: mode,
        matchedCount: shownHandles.length,
        shownHandles,
        productHandle,
      });
    }
  }

  closeModal() {
    if (!this.isOpen) return;
    this.dialog.classList.add('animate-close');

    this.isOpen = false;

    setTimeout(() => {
      document.querySelector('body').classList.remove('modal-open');
      this.dialog.close();
      this.dialog.classList.remove('animate-close');
      this.setPresentationMode('default');
    }, 200);
  }

  // M&M overflow: cards held back server-side by the shared 20-handle budget are
  // fetched here via the products-by-handle 'mm' context (its own budget,
  // valid-only) and appended to the popup, then re-aligned + re-sorted. Runs once
  // per dialog instance (a server re-render after ATC resets the guard).
  async _loadMmOverflow() {
    if (this.dataset.mmOverflowLoaded) return;
    this.dataset.mmOverflowLoaded = '1';   // guard before await — never double-fetch

    const track = this.querySelector('[data-mm-products]');
    const handles = (this.dataset.mmOverflowHandles || '').split(',').map((h) => h.trim()).filter(Boolean);
    const api = window.ProductsByHandle;
    if (!track || !handles.length || !api) return;

    const context = `mm:${this.dataset.mmSectionId || ''}:${this.dataset.mmPriceCap || '0'}`;
    let cards = await api.fetchCardsByHandle(handles, context);

    // Drop any whose product is already shown (dedupe by handle)
    const existing = new Set([...track.querySelectorAll('.product-card--mm')].map((c) => c.dataset.handle));
    cards = cards.filter((c) => c.dataset.handle && !existing.has(c.dataset.handle));
    if (!cards.length) return;

    cards.forEach((c) => track.appendChild(c));

    // Fill the opener preview from the same cards, up to its 8-thumbnail cap,
    // then recompute the grid column count (--ctl-cols) for the new total —
    // mirrors the server rule in complete-the-look-new.liquid: 4 cols when the
    // count is exactly 4 or >= 7, otherwise 3.
    // Look mode: the opener is a fixed main lookbook image + 2x2 preview, so we
    // must NOT append opener thumbnails or recompute --ctl-cols (that breaks the
    // 2x2). The popup cards above still grew from the overflow; only the opener
    // grid is left alone. Fallback (skus) keeps the dynamic 8-thumbnail preview.
    const grid = this.dataset.mmLookMode === 'true' ? null : this.querySelector('.ctl-new__grid');
    if (grid) {
      let shown = grid.querySelectorAll('.ctl-new__thumbnail').length;
      for (const card of cards) {
        if (shown >= 8) break;
        grid.appendChild(this._buildMmThumbnail(card));
        shown += 1;
      }
      const total = grid.querySelectorAll('.ctl-new__thumbnail').length;
      grid.style.setProperty('--ctl-cols', total === 4 || total >= 7 ? 4 : 3);
    }

    // Re-align sizes to the current variant and re-sort the now-complete set.
    document.querySelector('variant-selects')?.preselectCtlVariants?.();
    this._sortProducts();
    if (this.isOpen && this.classList.contains('popup--atc-mode')) {
      this.setPresentationMode('atc');   // re-hide OOS options on the new cards
      this._applyAtcProductLimit();
    }
  }

  // Build an opener thumbnail from a fetched M&M card (mirrors the server-rendered
  // .ctl-new__thumbnail: image[s] + the sort data attributes).
  _buildMmThumbnail(card) {
    const thumb = document.createElement('div');
    thumb.className = 'ctl-new__thumbnail';
    thumb.dataset.price = card.dataset.price || '0';
    thumb.dataset.bestseller = card.dataset.bestseller || '0';
    const imgs = [...card.querySelectorAll('.product-card--horizontal__image img')];
    imgs.slice(0, 2).forEach((img, i) => {
      const clone = img.cloneNode(true);
      clone.className = i === 1 ? 'ctl-new__thumbnail--secondary' : '';
      thumb.appendChild(clone);
    });
    return thumb;
  }

  _sortProducts() {
    const order = this.dataset.mmOrder;
    if (order !== 'price_desc' && order !== 'bestseller') return;

    const attr = order === 'bestseller' ? 'bestseller' : 'price';
    const byDesc = (a, b) =>
      parseFloat(b.dataset[attr] || 0) - parseFloat(a.dataset[attr] || 0);

    const grid = this.querySelector('.ctl-new__grid');
    if (grid) {
      [...grid.querySelectorAll('.ctl-new__thumbnail')]
        .sort(byDesc)
        .forEach((el) => grid.appendChild(el));
    }

    const products = this.querySelector('.ctl-new__products');
    if (products) {
      [...products.querySelectorAll('.product-card--mm')]
        .sort(byDesc)
        .forEach((el) => products.appendChild(el));
    }
  }

  _applyAtcProductLimit() {
    const max = parseInt(this.dataset.mmAtcMax || 4, 10);
    const products = [...this.querySelectorAll('.product-card--mm:not(.product-card--mm-atc-hidden):not(.product-card--mm-atc-price-excluded)')];
    products.forEach((product, i) => {
      product.classList.toggle('product-card--mm-hidden', i >= max);
    });
  }

  _resetProductLimit() {
    this.querySelectorAll('.product-card--mm.product-card--mm-hidden').forEach(el => {
      el.classList.remove('product-card--mm-hidden');
    });
  }
}


    window.CustomDialog = CustomDialog;
    if (!customElements.get('custom-dialog')) customElements.define('custom-dialog', CustomDialog);
  } catch (e) {
    console.warn('[custom-dialog] init failed (non-fatal):', e);
  }
})();
